import find from "lodash/find"
import indexOf from "lodash/indexOf"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import map from "lodash/map"
import toNumber from "lodash/toNumber"
import { useEffect, useRef } from "react"
import { ReactSVG } from "react-svg"
import { makeStyles } from "tss-react/mui"
import SiteSearchMobileHeader from "component-lib/SiteSearchMobileHeader/SiteSearchMobileHeader"
import getContentUrl from "utils-lib/getContentUrl"
import { withLocalization } from "hoc-lib/localization"
import { withAuthentication } from "hoc-lib/appAuthentication"
import { feUrls } from "utils-lib/builds"
import { getWindow } from "utils-lib/getWindow"
import { isLocalPage } from "utils-lib/isLocalPage"
import { isTeamsitePage } from "utils-lib/isTeamsitePage"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { handleEnterPress } from "utils-lib/handleEnterPress"
import { KEY_CODE_MAPPING } from "constants-lib/keyCodes"
import { ButtonPrimary, ButtonTextIcon, Link } from "../../../Base"
import MobileNavItems from "./components/MobileNavItems/MobileNavItems"

const MOBILE_HEADER_HEIGHT = 55

const strings = {
  en_CA: {
    SR_LOGO_TEXT: `Waste Management Home`,
    TARGET_LOGIN: `Log in`,
    TARGET_MYWM: `MY Wm`,
  },

  fr_CA: {
    SR_LOGO_TEXT: `Waste Management Résidence`,
    TARGET_LOGIN: `Log in`,
    TARGET_MYWM: `MY Wm`,
  },
}

const useStyles = makeStyles()((theme) => ({
  buttonTargetLogin: {
    color: `${theme.palette.primary.main2_0} !important`,
    backgroundColor: `${theme.palette.background.white} !important`,
    "&:focus": {
      opacity: `${0.1} !important`,
    },
  },
  buttonText: {
    color: `${theme.palette.primary.main2_0} !important`,
    fontWeight: 900,
  },
  link: {
    "&:focus": {
      outline: ` 2px auto -webkit-focus-ring-color !important`,
    },
  },
}))

export const MobileHeader = ({
  navigationDetails,
  closeMegaNav,
  copyright,
  hasFrPage,
  legalLinks,
  pageLocales = [],
  siteLanguage,
  isSingleAccount,
  utilityBarLinks,
  getCartDetails,
  ecommerceCart = {},
  isMenuOpen,
  logo,
  logoLink,
  onMenuClick,
  setParentState,
  strings,
  getAllCustomerServices,
  userManageAccount,
  myServices,
  setShowMobileSearch,
  showMobileSearch,
  logoAltText,
  isSupportSearch,
  indexList,
  queryIndex,
  numResults,
  numHighlightedResults,
  numberQueries,
  numCharacters,
  errorMessageTitle,
  errorMessageDescription,
  failureTitle,
  failureDescription,
  suggestedText,
  resultsText,
  seeAllResultsText,
  searchDelay,
  resultsIndex,
  filterType,
  isLoggedIn,
  searchPlaceholder,
  createProfileText = "",
}) => {
  const ssrWindow = getWindow()

  const isExternalLink =
    isMyWMApp || isTeamsitePage(ssrWindow.location.pathname)

  const { classes } = useStyles()
  const damPath = getContentUrl(`/content/dam/wm/icons`) // loading menu images from dam
  const truckIcon = "utilities/20x20/black/navigation/truck.svg"

  /* We are using refs here instead of state so that values can be passed to and updated from event listeners. */
  const prevScrollPos = useRef(ssrWindow.pageYOffset)

  const handleScroll = () => {
    const headerHeight = ssrWindow.document.querySelector(`.Header`)
      ? ssrWindow.document.querySelector(`.Header`).clientHeight
      : 0
    const alertHeight = ssrWindow.document.querySelector(`.GlobalNotifications`)
      ? ssrWindow.document.querySelector(`.GlobalNotifications`).clientHeight
      : 0
    const headerSpacingHeight = alertHeight + MOBILE_HEADER_HEIGHT

    if (ssrWindow.pageYOffset > 0) {
      const currentScrollPos = ssrWindow.pageYOffset
      const { scrollHeight } = ssrWindow.document.body
      const { offsetHeight } = ssrWindow.document.body

      const distanceFromBottom =
        scrollHeight - (currentScrollPos + offsetHeight)

      if (distanceFromBottom <= 200) {
        return
      }

      if (currentScrollPos > prevScrollPos) {
        ssrWindow.document.querySelector(
          `.Header`,
        ).style.top = `-${headerHeight}px`
        ssrWindow.document.querySelector(`.header-spacing`).style.height = `0px`
      } else if (
        prevScrollPos > currentScrollPos ||
        currentScrollPos <= headerHeight ||
        currentScrollPos === 0 ||
        prevScrollPos === 0
      ) {
        ssrWindow.document.querySelector(`.Header`).style.top = `0px`
        ssrWindow.document.querySelector(
          `.header-spacing`,
        ).style.height = `${headerSpacingHeight}px`
      }

      prevScrollPos.current = currentScrollPos
    } else {
      const headerSpacingElement =
        ssrWindow.document.querySelector(`.header-spacing`)

      headerSpacingElement &&
        (headerSpacingElement.style.height = `${headerSpacingHeight}px`)
    }
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const customerIds = map(
      get(userManageAccount, `linkedAccounts`, []),
      (account) => get(account, `custAccountId`, ``),
    )

    !isEmpty(customerIds) &&
      !get(myServices, `getCustomerServicesState`, ``) &&
      getAllCustomerServices(customerIds)

    // To hide meganav while scrolling down and show white meganav while scrolling up
    if (!isMenuOpen) {
      ssrWindow.addEventListener(`scroll`, handleScroll, false)

      return () => {
        ssrWindow.removeEventListener(`scroll`, handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    ssrWindow.removeEventListener(`scroll`, handleScroll)
    ssrWindow.addEventListener(`scroll`, handleScroll)
  }, [ssrWindow.location.pathname])

  const cartCount = get(ecommerceCart, `count`, ``)

  const pathArr = ssrWindow.location.pathname.split("/")

  const handleKeyDown = (e) => {
    if (e.keyCode === KEY_CODE_MAPPING.TAB && e.shiftKey && isMenuOpen) {
      e.preventDefault()
      ssrWindow.document.querySelector(`[aria-label="WM Home"]`)?.focus()
    } else if (e.keyCode === KEY_CODE_MAPPING.TAB && isMenuOpen) {
      e.preventDefault()
      ssrWindow.document.querySelector(`[aria-label="Search"]`)?.focus()
    } else {
      handleEnterPress(e, () => onMenuClick(!isMenuOpen))
    }
  }

  function renderMenu() {
    return (
      <div className="d-flex justify-content-between">
        {!isMenuOpen && (
          <>
            {indexOf(pathArr, `support`) === -1 &&
              indexOf(pathArr, `articles`) === -1 && (
                <div
                  tabIndex="0"
                  className="MobileHeader__search-icon mr-3 wm-cursor-pointer link"
                  onClick={() => {
                    setShowMobileSearch(true)
                  }}
                >
                  <ReactSVG
                    wrapper="span"
                    src={getContentUrl("/content/dam/wm/icons/search-icon.svg")}
                  />
                </div>
              )}
            {map(utilityBarLinks, (item, index) => {
              const utilityType = get(item, `utilityType`, ``)
              const analyticsKey = get(item, `analytics_key`, ``)
              const cartIcon = get(item, `cartIcon`, ``)

              if (utilityType === `cart` && cartCount) {
                return (
                  <ButtonTextIcon
                    analytics_key={analyticsKey}
                    className="maax-font-weight-regular wm-link-light-green"
                    key={index}
                    onClick={() => {
                      getCartDetails().then((url) => {
                        if (url) {
                          ssrWindow.location = url
                        }
                      })
                    }}
                    style={{
                      marginRight: 7,
                    }}
                  >
                    <ReactSVG
                      wrapper="span"
                      className="svg-container pr-2 d-flex align-items-center"
                      src={getContentUrl(cartIcon)}
                    />
                    {utilityType === `cart` && (
                      <div className="counter">{cartCount}</div>
                    )}
                  </ButtonTextIcon>
                )
              }
              return null
            })}
          </>
        )}
        <div
          role="button"
          aria-expanded={isMenuOpen}
          onClick={() => onMenuClick(!isMenuOpen)}
          tabIndex="0"
          onKeyDown={(e) => {
            handleKeyDown(e)
          }}
          className={`hamburger-menu d-flex justify-content-center mr-2 mr-md-0 position-relative ${classes.link}`}
          style={{
            top: 2,
            width: 26,
            height: "1.2rem",
          }}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          <span>
            {isMenuOpen ? (
              <ReactSVG
                wrapper="span"
                src={`${damPath}/green-close-x-icon.svg`}
              />
            ) : (
              <ReactSVG
                wrapper="span"
                src={`${damPath}/green-hamburger-menu-icon.svg`}
              />
            )}
          </span>
          {/* Only visible to screen reader */}
          <span className="sr-only">
            {isMenuOpen ? "Close menu" : "Open menu"}
          </span>
        </div>
      </div>
    )
  }

  const viewScheduleObj = find(
    utilityBarLinks,
    (item) => item.utilityType === `viewschedule`,
  )

  return (
    <div
      className="mobileNav sub-header"
      data-testid="MobileHeader"
      style={{
        boxShadow: isMenuOpen
          ? "none"
          : "0 2px 6px 0 rgba(175, 181, 186, 0.3), 0 -1px 6px 0 rgba(175, 181, 186, 0.3)",
      }}
    >
      <SiteSearchMobileHeader
        isSearchOpen={showMobileSearch}
        isSupportSearch={isSupportSearch}
        setIsSearchOpen={setShowMobileSearch}
        indexList={indexList}
        resultsIndex={resultsIndex}
        suggestionIndex={resultsIndex}
        queryIndex={queryIndex}
        filterType={filterType}
        searchPlaceholder={searchPlaceholder}
        numResults={toNumber(numResults)}
        numHighlightedResults={toNumber(numHighlightedResults)}
        numberQueries={toNumber(numberQueries)}
        numCharacters={toNumber(numCharacters)}
        errorMessageTitle={errorMessageTitle}
        errorMessageDescription={errorMessageDescription}
        failureTitle={failureTitle}
        failureDescription={failureDescription}
        suggestedText={suggestedText}
        resultsText={resultsText}
        seeAllResultsText={seeAllResultsText}
        searchDelay={searchDelay}
        language={siteLanguage?.language}
      />
      <div id="page-wrap">
        <div
          className={`MobileHeader__hamburger-menu w-100 position-absolute ${
            isMenuOpen ? `MobileHeader__hamburger-menu--open` : ``
          }`}
        >
          <MobileNavItems
            navigationDetails={navigationDetails}
            closeMegaNav={closeMegaNav}
            isSingleAccount={isSingleAccount}
            utilityBarLinks={utilityBarLinks}
            getCartDetails={getCartDetails}
            ecommerceCart={ecommerceCart}
            createProfileText={createProfileText}
            onMenuClick={() => {
              onMenuClick(false)
            }}
            logo={logo}
            logoAltText={logoAltText}
            logoLink={logoLink}
            copyright={copyright}
            hasFrPage={hasFrPage}
            legalLinks={legalLinks}
            pageLocales={pageLocales}
            siteLanguage={siteLanguage}
            setParentState={setParentState}
            searchPlaceholder={searchPlaceholder}
            setIsSearchOpen={setShowMobileSearch}
            isLocalPage={isLocalPage}
          />
        </div>
        <div className="container mobile-nav-container d-flex align-items-center justify-content-between">
          <div className="app-logo ml-2 ml-md-0">
            <Link
              href={logoLink}
              isExternalLink={isExternalLink}
              tabIndex="0"
              className={classes.link}
              ariaLabel="WM Home"
            >
              <span className="sr-only">{strings.SR_LOGO_TEXT}</span>
              <img
                className="footer-logo"
                src={getContentUrl(logo)}
                alt={logoAltText}
              />
            </Link>
          </div>
          {!isLoggedIn ? (
            <div
              id="targetLogin"
              className={`${classes.buttonText}`}
              style={{ display: "none" }}
            >
              <ButtonPrimary
                className={`${classes.buttonTargetLogin}`}
                onClick={() => {
                  ssrWindow.location = feUrls.LOGIN
                }}
              >
                {strings.TARGET_LOGIN}
              </ButtonPrimary>
            </div>
          ) : (
            <div
              id="targetMywm"
              style={{ color: "#1C8200", fontWeight: 900, display: "none" }}
            >
              <ButtonPrimary
                className={`${classes.buttonTargetLogin}`}
                onClick={() => {
                  const languagePath = getLanguageRoute(siteLanguage.language)
                  ssrWindow.location.pathname = `${languagePath}/${feUrls.MYWM_DASHBOARD}`
                }}
              >
                {strings.TARGET_MYWM}
              </ButtonPrimary>
            </div>
          )}
          {viewScheduleObj ? (
            <div
              className="d-flex justify-content-center"
              style={{
                width: `7.12rem`,
                height: `24px`,
              }}
            >
              {!isMenuOpen && (
                <Link
                  analytics_key={get(viewScheduleObj, `analyticsKey`, ``)}
                  className={`mr-3 ${classes.link}`}
                  isExternalLink={
                    !isMyWMApp ||
                    (isMyWMApp && isTeamsitePage(ssrWindow.location.pathname))
                  }
                  tabindex="0"
                  onKeyPress={(e) =>
                    handleEnterPress(e, get(viewScheduleObj, `utilityLink`, ``))
                  }
                  href={get(viewScheduleObj, `utilityLink`, ``)}
                  ariaLabel="view pickup eta"
                >
                  <ReactSVG
                    wrapper="span"
                    className="d-flex truck-link"
                    src={`${damPath}/${truckIcon}`}
                  />
                </Link>
              )}

              {renderMenu()}
            </div>
          ) : (
            renderMenu()
          )}
        </div>
      </div>
    </div>
  )
}

// // Note: React 19 no-longer supports propTypes. Leaving this for reference.
// MobileHeaderLegacy.propTypes = {
//   navigationDetails: PropTypes.array.isRequired,
//   closeMegaNav: PropTypes.func.isRequired,
//   copyright: PropTypes.string.isRequired,
//   // customerAddress: PropTypes.object.isRequired,
//   hasFrPage: PropTypes.bool.isRequired,
//   legalLinks: PropTypes.array.isRequired,
//   pageLocales: PropTypes.array,
//   siteLanguage: PropTypes.object.isRequired,
//   isSingleAccount: PropTypes.bool.isRequired,
//   utilityBarLinks: PropTypes.array.isRequired,
//   getCartDetails: PropTypes.func.isRequired,
//   ecommerceCart: PropTypes.object,
//   isMenuOpen: PropTypes.bool.isRequired,
//   logo: PropTypes.string.isRequired,
//   logoLink: PropTypes.string.isRequired,
//   // unused prop, removed from the migrated component
//   language: PropTypes.string.isRequired,
//   onMenuClick: PropTypes.func.isRequired,
//   setParentState: PropTypes.func.isRequired,
//   //  languageRoute: PropTypes.string.isRequired,
//   strings: PropTypes.object.isRequired,
//   // userState: PropTypes.number.isRequired,
//   getAllCustomerServices: PropTypes.func.isRequired,
//   userManageAccount: PropTypes.object.isRequired,
//   myServices: PropTypes.object.isRequired,
//   setShowMobileSearch: PropTypes.func.isRequired,
//   showMobileSearch: PropTypes.bool.isRequired,
//   logoAltText: PropTypes.string.isRequired,
//   isSupportSearch: PropTypes.bool.isRequired,
//   indexList: PropTypes.array.isRequired,
//   queryIndex: PropTypes.string.isRequired,
//   // searchBarPlaceholderText: PropTypes.string.isRequired,
//   numResults: PropTypes.number.isRequired,
//   numHighlightedResults: PropTypes.number.isRequired,
//   numberQueries: PropTypes.number.isRequired,
//   numCharacters: PropTypes.number.isRequired,
//   errorMessageTitle: PropTypes.string.isRequired,
//   errorMessageDescription: PropTypes.string.isRequired,
//   failureTitle: PropTypes.string.isRequired,
//   failureDescription: PropTypes.string.isRequired,
//   suggestedText: PropTypes.string.isRequired,
//   resultsText: PropTypes.string.isRequired,
//   seeAllResultsText: PropTypes.string.isRequired,
//   searchDelay: PropTypes.number.isRequired,
//   resultsIndex: PropTypes.string.isRequired,
//   filterType: PropTypes.string.isRequired,
//   isLoggedIn: PropTypes.bool.isRequired,
//   searchPlaceholder: PropTypes.string.isRequired,
//   createProfileText: PropTypes.string,
// }

// // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// MobileHeaderLegacy.defaultProps = {
//   pageLocales: [],
//   ecommerceCart: {},
//   createProfileText: "",
// }

export default withLocalization(strings)(withAuthentication(MobileHeader))
